
import AppModelColumnSelect from "@/components/models/ColumnSelect.vue";
import { Browse } from "@/models/browse";
import { IViewColumnBooleanIconSettings, ViewColumn, ViewColumnAlignment } from "@/utils/views/view-column";
import Vue from "vue";
import { TranslateResult } from "vue-i18n";
import { ISelectItem } from "winfakt-vue-components";
import { ModelNamedProperty } from "../../utils/models/named-property";
import AppColumnEditClickActionOptions from "./ColumnEditClickActionOptions.vue";
import AppColumnEditCustomBooleanIconAdvancedSettings from "./ColumnEditCustomBooleanIconAdvancedSettings.vue";
import AppBrowseColumnSelectJsonProperty from "@/components/models/BrowseColumnSelectJsonProperty.vue";

export default Vue.extend({
	data() {
		return {
			decimals: null as null | number,
			columnWidth: 0,
			selectedProp: null as ModelNamedProperty | null,
			alignment: null as null | number,
			customBoolean: {
				mode: null as null | "icons" | "text",
				textTrue: this.$ct("common.yes").toString(),
				textFalse: this.$ct("common.no").toString(),
				booleanSettings: {
					IconTrue: "check",
					IconFalse: "times",
					IconTrueColor: "#009900",
					IconFalseColor: "#dedede"
				} as IViewColumnBooleanIconSettings
			},
			ready: false
		};
	},
	props: {
		add: {
			type: Boolean
		},
		column: {
			type: Object as () => ViewColumn,
			required: true
		},
		browse: {
			type: Object as () => Browse,
			required: true
		}
	},
	computed: {
		properties(): ISelectItem<string>[] {
			return this.browse.type.getBrowseFields().map(f => {
				return {
					text: f.getPropertyTranslation,
					value: f.path
				};
			});
		},
		selectedPropName: {
			get(): string {
				if (!this.selectedProp) return "";
				return this.selectedProp.path;
			},
			set(val: string) {
				let found = this.properties.find(p => p.value == val);
				if (!found) {
					this.selectedProp = null;
				} else {
					if (!this.browse.type) return;
					this.selectedProp = this.browse.type.getBrowseFields().find(f => f.path == val) || null;
					this.decimals = null;
				}
			}
		},
		isDecimalSettingAllowed():boolean {
			if (!this.selectedProp) return false;
			if(this.selectedProp.field.type != "number"){
				return false;
			};
			return true;
		},
		alignItems():ISelectItem<ViewColumnAlignment | null>[] {
			let defaultValue = "";
			if (this.selectedProp) {
				if (this.selectedProp.field.rightAlign) {
					defaultValue = `(${this.$ct("alignment-item.align-right")})`;
				}else if (this.selectedProp.field.type == "number"){
					defaultValue = `(${this.$ct("alignment-item.align-right")})`;
				}else if (this.selectedProp.field.type == "boolean"){
					defaultValue = `(${this.$ct("alignment-item.align-center")})`;
				}else{
					defaultValue = `(${this.$ct("alignment-item.align-left")})`;
				}
			}

			return [
				{
					text: this.$ct("alignment-item.use-default", {val: defaultValue}),
					value: null
				},
				{
					text: this.$ct("alignment-item.align-left"),
					value: ViewColumnAlignment.LEFT
				},
				{
					text: this.$ct("alignment-item.align-center"),
					value: ViewColumnAlignment.CENTER
				},
				{
					text: this.$ct("alignment-item.align-right"),
					value: ViewColumnAlignment.RIGHT
				}
			];
		},
		isBoolean():boolean {
			if (!this.selectedProp) return false;
			return this.selectedProp.field.type == "boolean";
		},
		hasClickAction():boolean {
			if (!this.selectedProp) return false;
			let action = this.selectedProp.field.clickAction;
			if (!action) return false;
			if (action.cannotBeDisabled) return false;
			if (!action.canBeUsed) return true;
			return action.canBeUsed(this.browse, this.column.Property);
		},
		enableClickActionText():TranslateResult {
			if (!this.selectedProp) return "";
			let clickAction = this.selectedProp.field.clickAction;
			if (!clickAction) return "";
			return this.$ct("enable-click-action", {name: clickAction.getName()});
		},
		isJson():boolean{
			if (!this.selectedProp) return false;
			return this.selectedProp.field.type == "json";
		}
	},
	watch: {
		selectedProp: {
			immediate: true,
			handler(){
				if (!this.isDecimalSettingAllowed) {
					this.decimals = null;
				}
				if (!this.isJson && this.ready) {
					this.column.JsonProperty = "";
				}
			}
		},
		hasClickAction(){
			if (!this.hasClickAction) {
				this.column.EnableClickAction = false;
			}
		},
	},
	methods: {
		cancel() {
			this.$emit("cancel");
		},
		save() {
			if (!this.selectedProp) {
				return this.$wf.notify(this.$ct("error-no-column-selected").toString(), "danger", 3000, "exclamation-triangle");
			}
			this.column.Property = this.selectedProp.path;
			this.column.IsComputed = this.selectedProp.field.isComputed;
			let pathParts = this.column.Property.split(".");
			pathParts.pop();
			let pathPrefix = pathParts.join(".");
			if (this.column.IsComputed) {
				this.column.ComputedPreloads = (this.selectedProp.field.dependencies || []).map(d=>{
					if (!pathPrefix) {
						return d;
					}
					return `${pathPrefix}.${d}`;
				});
			}
			if (this.column.Name == "") {
				this.column.Name = (this.properties.find(prop => prop.value == this.column.Property) || {text: ""}).text.toString();
			}
			this.column.Width = this.columnWidth == 0 ? "200px" : (this.columnWidth + "px");
			this.column.Decimals = this.decimals;
			this.column.RightAlign = this.alignment;
			this.saveSetCustomBoolean();
			this.$emit("save");
		},
		setCustomBooleanIcons(){
			this.disableCustomBoolean();
			this.customBoolean.mode = "icons";
		},
		onPropName(name: string) {
			this.column.Name = name.replace(/<([^>]+?)([^>]*?)>(.*?)<\/\1> /ig, "");
		},
		onInput(name: string) {
			this.column.Name = name;
		},
		toggleDecimals(val:boolean){
			if (!val){
				this.decimals = 0;
			}else{
				this.decimals = null;
			}
		},
		disableCustomBoolean(){
			this.customBoolean.mode = null;
			this.column.IconTrue = "";
			this.column.IconFalse = "";
			this.column.IconTrueColor = "";
			this.column.IconFalseColor = "";
			this.column.TextTrue = "";
			this.column.TextFalse = "";
		},
		setCustomBooleanText(){
			this.disableCustomBoolean();
			this.customBoolean.mode = "text";
		},
		saveSetCustomBoolean(){
			this.column.IconFalse = "";
			this.column.IconFalseColor = "";
			this.column.IconTrue = "";
			this.column.IconTrueColor = "";
			this.column.TextTrue = "";
			this.column.TextFalse = "";
			if (this.customBoolean.mode == "icons"){
				this.column.IconTrue = this.customBoolean.booleanSettings.IconTrue;
				this.column.IconFalse = this.customBoolean.booleanSettings.IconFalse;
				this.column.IconTrueColor = this.customBoolean.booleanSettings.IconTrueColor;
				this.column.IconFalseColor = this.customBoolean.booleanSettings.IconFalseColor;
			}else if (this.customBoolean.mode == "text") {
				this.column.TextTrue = this.customBoolean.textTrue;
				this.column.TextFalse = this.customBoolean.textFalse;
			}
		}
	},
	async mounted() {
		if (this.column.Width) {
			this.columnWidth = parseInt(this.column.Width);
		}
		if (!this.browse.type) {
			this.ready = true;
			return;
		};
		this.selectedProp = this.browse.type.getBrowseFields().find(f => f.path == this.column.Property) || null;
		this.decimals = this.column.Decimals;
		this.alignment = this.column.RightAlign;
		if (this.column.IconTrue || this.column.IconFalse) {
			this.customBoolean.mode = "icons";
			this.customBoolean.booleanSettings.IconFalse = this.column.IconFalse;
			this.customBoolean.booleanSettings.IconTrue = this.column.IconTrue;
			this.customBoolean.booleanSettings.IconFalseColor = this.column.IconFalseColor;
			this.customBoolean.booleanSettings.IconTrueColor = this.column.IconTrueColor;
		}else if (this.column.TextTrue || this.column.TextFalse) {
			this.customBoolean.mode = "text";
			this.customBoolean.textTrue = this.column.TextTrue;
			this.customBoolean.textFalse = this.column.TextFalse;
		}
		this.ready = true;
	},
	components: {
		AppModelColumnSelect,
		AppColumnEditClickActionOptions,
		AppColumnEditCustomBooleanIconAdvancedSettings,
		AppBrowseColumnSelectJsonProperty
	},
	i18nEx: {
		componentPrefix: "components.view-editor"
	}
});
